// "Agrupar con IA" (carga masiva): arma thumbnails chicos de las imágenes
// locales, se los manda al backend (POST /v1/products/group-images) y
// devuelve los grupos saneados. La aplicación sobre la lista vive en
// useBulkUpload.applyAiGrouping — acá solo transporte.

import { atlasBackendFetch } from "./atlasBackend";
import { buildThumbnailBlob } from "../utils/thumbnail";
import type { GrupoIaRespuesta } from "../utils/bulkUnits";

// 256px alcanza para que el modelo compare productos y mantiene el payload
// chico (~10-20KB por imagen): la tanda entera viaja en un solo POST.
const LADO_THUMB_IA = 256;

const MENSAJE_GENERICO =
  "No pudimos agrupar las imágenes ahora. Probá de nuevo en un rato.";

const blobABase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result ?? "");
      const coma = url.indexOf(",");
      if (coma < 0) reject(new Error("dataURL inválida"));
      else resolve(url.slice(coma + 1));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("no se pudo leer el thumbnail"));
    reader.readAsDataURL(blob);
  });

export async function agruparImagenesConIa(
  items: Array<{ id: string; file: File }>,
  signal?: AbortSignal,
): Promise<GrupoIaRespuesta[]> {
  const imagenes = (
    await Promise.all(
      items.map(async (it) => {
        // buildThumbnailBlob es best-effort: si una imagen no se puede
        // procesar queda fuera del pedido y sigue como su propia unidad.
        const thumb = await buildThumbnailBlob(it.file, LADO_THUMB_IA);
        if (!thumb) return null;
        return {
          id: it.id,
          mime: thumb.type || "image/jpeg",
          data: await blobABase64(thumb),
        };
      }),
    )
  ).filter((im): im is { id: string; mime: string; data: string } => im !== null);

  if (imagenes.length < 2) {
    throw new Error("No pudimos preparar las imágenes para agrupar.");
  }

  // Un fallo de red (sin conexión, CORS, el servidor dormido) llega como TypeError con
  // "Failed to fetch": al usuario no le dice nada. Pasa al genérico; un timeout del
  // `AbortSignal` tiene su propio mensaje.
  let res: Response;
  try {
    res = await atlasBackendFetch(
      "/v1/products/group-images",
      { imagenes },
      signal ? { signal } : undefined,
    );
  } catch (e) {
    if (e instanceof DOMException && (e.name === "TimeoutError" || e.name === "AbortError")) {
      throw new Error("Agrupar tardó demasiado. Probá de nuevo con menos imágenes.");
    }
    throw new Error(MENSAJE_GENERICO);
  }
  if (!res.ok) {
    let message = MENSAJE_GENERICO;
    try {
      const j = (await res.json()) as { message?: unknown };
      // Los 400 de validación traen detalle técnico — al usuario le sirve más
      // el genérico; solo los mensajes ya redactados para humanos pasan.
      if (res.status !== 400 && typeof j.message === "string" && j.message) {
        message = j.message;
      }
    } catch {
      /* body no-JSON → genérico */
    }
    throw new Error(message);
  }

  const json = (await res.json().catch(() => null)) as {
    grupos?: unknown;
  } | null;
  const grupos = Array.isArray(json?.grupos) ? json?.grupos : null;
  if (!grupos) throw new Error(MENSAJE_GENERICO);
  return grupos
    .filter(
      (g): g is { ids: unknown[]; nombre?: unknown } =>
        !!g && typeof g === "object" && Array.isArray((g as { ids?: unknown }).ids),
    )
    .map((g) => ({
      ids: g.ids.filter((x): x is string => typeof x === "string"),
      nombre: typeof g.nombre === "string" ? g.nombre : "",
    }));
}
